import { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../../../firebase/config'; // Ajustá esta ruta relativa según tu estructura real de carpetas
import { doc, getDoc, getDocs, collection, query, where, documentId } from 'firebase/firestore';

/**
 * useDataHydration - Hook universal de arquitectura para hidratación masiva y asíncrona.
 * Escucha una lista de datos crudos y resuelve automáticamente en background los IDs de
 * Clientes y Pólizas relacionados, utilizando caché en memoria (useRef) para evitar
 * llamadas repetidas e innecesarias a Firestore, garantizando "Inmunidad de Costos".
 * * @param {Array} rawData - Listado crudo de documentos operativos (cobranzas, endosos, siniestros).
 */
export const useDataHydration = (rawData = []) => {
    // --- ESTADOS DE CACHÉ ACTIVO ---
    const [clientsCache, setClientsCache] = useState({});
    const [policiesCache, setPoliciesCache] = useState({});
    const [isHydrating, setIsHydrating] = useState(false);

    // --- REFS DE SEGURIDAD (Evitan lecturas duplicadas en renders masivos continuos) ---
    const clientsCacheRef = useRef({});
    const policiesCacheRef = useRef({});

    /**
     * hydrate - Procesa una lista de registros, detecta IDs faltantes y los resuelve en lotes.
     */
    const hydrate = useCallback(async (list) => {
        if (!list || list.length === 0) return;

        const missingClientIds = [];
        const missingPolicyKeys = [];

        // 1. Detectar qué IDs de clientes y claves de pólizas no se encuentran en los cachés locales
        list.forEach(item => {
            const clientId = item.clienteId;
            const polizaId = item.polizaId;

            if (clientId && !clientsCacheRef.current[clientId]) {
                missingClientIds.push(clientId);
            }
            if (clientId && polizaId) {
                const pKey = `${clientId}|${polizaId}`;
                if (!policiesCacheRef.current[pKey]) {
                    missingPolicyKeys.push(pKey);
                }
            }
        });

        // Remoción de duplicados en caliente antes de ir a Firestore
        const uniqueClientIds = [...new Set(missingClientIds)];
        const uniquePolicyKeys = [...new Set(missingPolicyKeys)];

        // Si no hay datos faltantes que resolver, saltamos la consulta de red (Lecturas de base = 0)
        if (uniqueClientIds.length === 0 && uniquePolicyKeys.length === 0) {
            return;
        }

        setIsHydrating(true);
        const newClients = {};
        const newPolicies = {};

        try {
            // 2. RESOLVER CLIENTES EN LOTES (Chunks de 30)
            // Firestore limita las consultas de tipo 'in' a un máximo de 30 elementos por petición.
            if (uniqueClientIds.length > 0) {
                const chunks = [];
                for (let i = 0; i < uniqueClientIds.length; i += 30) {
                    chunks.push(uniqueClientIds.slice(i, i + 30));
                }

                await Promise.all(chunks.map(async (chunk) => {
                    const q = query(
                        collection(db, 'public/data/clientes'), 
                        where(documentId(), 'in', chunk)
                    );
                    const snap = await getDocs(q);
                    snap.forEach(d => {
                        const data = { id: d.id, ...d.data() };
                        newClients[d.id] = data;
                        clientsCacheRef.current[d.id] = data; // Sello de caché inmediato
                    });

                    // ◄ APAGADO DE INFINITE LOOPS: Marcamos los IDs no encontrados en Firestore como huérfanos
                    chunk.forEach(id => {
                        if (!newClients[id]) {
                            const fallback = { id, notFound: true, nombre: 'Asegurado', apellido: 'Desasociado' };
                            newClients[id] = fallback;
                            clientsCacheRef.current[id] = fallback;
                        }
                    });
                }));
            }

            // 3. RESOLVER PÓLIZAS EN PARALELO
            if (uniquePolicyKeys.length > 0) {
                await Promise.all(uniquePolicyKeys.map(async (key) => {
                    const [cId, pId] = key.split('|');
                    try {
                        const pSnap = await getDoc(doc(db, `public/data/clientes/${cId}/polizas`, pId));
                        if (pSnap.exists()) {
                            const data = { id: pSnap.id, clienteId: cId, ...pSnap.data() };
                            newPolicies[key] = data;
                            policiesCacheRef.current[key] = data; // Sello de caché inmediato
                        } else {
                            // ◄ APAGADO DE INFINITE LOOPS: Sello de huérfano para pólizas no encontradas
                            const fallback = { id: pId, clienteId: cId, notFound: true, nPoliza: 'PENDIENTE', compania: 'S/D' };
                            newPolicies[key] = fallback;
                            policiesCacheRef.current[key] = fallback;
                        }
                    } catch (e) {
                        console.error(`Error hidratando póliza patrimonial #${pId} para el cliente ${cId}:`, e);
                        const fallback = { id: pId, clienteId: cId, notFound: true, nPoliza: 'PENDIENTE', compania: 'S/D' };
                        newPolicies[key] = fallback;
                        policiesCacheRef.current[key] = fallback;
                    }
                }));
            }

            // 4. ACTUALIZAR ESTADO DE FORMA SEGURA Y ATÓMICA
            if (Object.keys(newClients).length > 0) {
                setClientsCache(prev => ({ ...prev, ...newClients }));
            }
            if (Object.keys(newPolicies).length > 0) {
                setPoliciesCache(prev => ({ ...prev, ...newPolicies }));
            }

        } catch (error) {
            console.error("Error crítico durante la hidratación de datos en background:", error);
        } finally {
            setIsHydrating(false);
        }
    }, []);

    // Hidratación automática reactiva ante el cambio o alimentación de los datos crudos del listener
    useEffect(() => {
        if (rawData && rawData.length > 0) {
            hydrate(rawData);
        }
    }, [rawData, hydrate]);

    return {
        clientsCache,
        policiesCache,
        isHydrating,
        clientsCacheRef,
        policiesCacheRef,
        hydrate
    };
};